import {
	Component, Input, Inject, LOCALE_ID, Output, EventEmitter,
	AfterViewInit, ElementRef, HostListener, OnDestroy
} from '@angular/core';
import { Location } from '@angular/common';
import { registerFilePreview } from '@onviobr/file-preview';
import { DocumentEvent, IDocument1, IDocument2 } from './file-preview.model';
import { register as registerDmsViewer } from '@onviobr/toolbox-viewer/publish';
import {
	defaultOptions as defaultWebViewerOptions, PdfTronElement as WebViewerElement,
	Options, AnnotatableFileMimeType
} from '@onviobr/toolbox-viewer/publish/dms-viewer-interface';
import { viewControlsDropdown, cogMenu, WebViewerActionSet } from '@onviobr/toolbox-file-viewer';
import { isWebViewerClientSupported } from '@onviobr/toolbox-viewer/publish/supported-types';
import { WEB_VIEWER_LIBRARY_PATH } from '@onviobr/document-components';
import { TranslateService } from '@ngx-translate/core';
import { ContactService, DocumentService } from '@onviobr/toolbox-document-services/dist/esm5';
import { getBindings } from '@onviobr/services/session';
import { FileDrawer, LockContext, LockType } from '@onviobr/toolbox-document-services/dist/esm5/models';
import { OpenItemService } from '@onviobr/document-components';
import { CancellationTokenSource, ToolbarServicesFacade } from './file-preview-toolbar-services-facade';
import { FilePreviewToolbarActionFactory } from './file-preview-toolbar-action-factory';
import { has as hasIac } from '@onviobr/services/access';

registerFilePreview();
registerDmsViewer();

@Component({
	selector: 'app-file-preview',
	templateUrl: './file-preview.component.html',
	styleUrls: ['./file-preview.component.css']
})
export class FilePreviewComponent implements AfterViewInit, OnDestroy {
	private _documents: IDocument2[];
	private _currentIndex: number | undefined;
	private _annotationsAllowed = false;
	private _documentMimeType?: string;
	private _cancellationTokenSource?: CancellationTokenSource;

	public document?: IDocument2;
	public options = defaultWebViewerOptions;

	public userDisplayAs: string;
	public userContactId: string;
	public getBindings = getBindings;
	public viewerIsLoading = false;
	public toolbarActions?: WebViewerActionSet;
	public shouldNotShowNewInsertScan = true;
	hasIac = hasIac;

	@Input() public drawer?: FileDrawer;
	@Input() public usePdfTron = false;
	@Input() public hideDelete = true;
	@Input() public hideHelp = true;
	@Input() public hideBookmarks = false;
	@Input() public hideClose = false;
	@Input() public hidePrint = false;
	@Input() public isCurrentDevelopment = false;
	@Input() public showNavigationArrows = true;
	@Input() public errorCallback: () => void;
	@Input() public closeCallback: () => void;
	@Input() public headerButtonClickCallback: () => void;
	@Input() public initialZoom = 1.2;
	@Input() public finalizationFunctionContainer: any;
	@Input() public showDownloadModal = false;
	@Input() public hideDocumentPreview = false;
	@Output() public documentNav: EventEmitter<IDocument1 | IDocument2> = new EventEmitter();
	@Output() public loaded: EventEmitter<boolean> = new EventEmitter();
	@Output() public downloadModalClosed: EventEmitter<void> = new EventEmitter();
	@Output() public documentUpdateAction: EventEmitter<void> = new EventEmitter();
	@Output() public actionDoneOnDocument: EventEmitter<{ id: string; action: DocumentEvent }> = new EventEmitter();

	@Input()
	public set annotationsAllowed(value: boolean) {
		this._annotationsAllowed = value;
		this.configureWebViewerAppearance();
	}

	@Input()
	public set documents(value: IDocument2[]) {
		this._documents = value;
		this.setPreviewDocumentIndex();
	}

	public get documents(): IDocument2[] {
		return this._documents;
	}

	@Input()
	public set currentIndex(value: number) {
		this._currentIndex = value;
		this.setPreviewDocumentIndex();
	}

	public get currentIndex(): number {
		return this._currentIndex;
	}

	@Input()
	public set toolbarServicesFacade(value: ToolbarServicesFacade) {
		if (!!value) {
			const actionsFactory = new FilePreviewToolbarActionFactory(
				this.getBindings,
				this.documentService,
				this.openItemService,
				value,
				() => this.documents as any as Array<IDocument2>,
				() => this.document,
				this.onActionDoneOnDocument.bind(this),
				this.drawer,
			);
			this.toolbarActions = actionsFactory.createToolbarActions();
			this._cancellationTokenSource = actionsFactory;
		}
	}

	@HostListener('document:keydown.escape', ['$event']) onKeydownHandler() {
		this.closeCallback();
	}

	constructor(
		@Inject(LOCALE_ID) public lang: string,
		@Inject(WEB_VIEWER_LIBRARY_PATH) webViewerLibPath: string,
		public el: ElementRef,
		public contactService: ContactService,
		public translate: TranslateService,
		public documentService: DocumentService,
		public openItemService: OpenItemService,
		public location: Location
	) {
		this.options.webViewerLibPath = webViewerLibPath;
		this.options.locale = lang;

		this.viewerIsLoading = true;
	}

	public ngAfterViewInit(): void {
		this.loaded.emit(true);
		this.setUserDisplayAsAndContactId();
		this.setPreviewDocumentIndex();

		this.options.strings.printButton = this.translate.instant(/* i18nextract */'StaffApp.DMS.Viewer.Print');
		this.options.strings.closeButton = this.translate.instant(/* i18nextract */'Bluemoon.Practice.Common.Actions.Close');
		this.options.strings.navigationButtonRight =
			this.translate.instant(/* i18nextract */'StaffApp.DMS.Viewer.NavigationButton.NextDocument');
		this.options.strings.navigationButtonLeft =
			this.translate.instant(/* i18nextract */'StaffApp.DMS.Viewer.NavigationButton.PreviousDocument');
		this.options.strings.pageNavOverlayButtonLeft =
			this.translate.instant(/* i18nextract */'Bluemoon.Practice.Common.Buttons.Previous');
		this.options.strings.pageNavOverlayButtonRight = this.translate.instant(/* i18nextract */'Bluemoon.Practice.Common.Buttons.Next');
		this.options.strings.previewNotAvailable = this.translate.instant(/* i18nextract */'StaffApp.DMS.Viewer.PreviewNotAvailable');
		this.options.strings.downloadButton = this.translate.instant(/* i18nextract */'Bluemoon.DMS.Toolbar.Download');
		this.options.strings.movedOrDeletedErrorMessage =
			this.translate.instant(/* i18nextract */'StaffApp.DMS.Viewer.ErrorMessage.MovedOrDeleted');
		this.options.strings.cannotBeSavedErrorMessage =
			this.translate.instant(/* i18nextract */'StaffApp.DMS.Viewer.ErrorMessage.CannotBeSaved');
		this.options.strings.serverErrorMessage = this.translate.instant(/* i18nextract */'StaffApp.DMS.Viewer.ErrorMessage.ServerError');
		this.options.strings.documentLocked = this.translate.instant(/* i18nextract */'StaffApp.DMS.Viewer.DocumentLocked');
		this.options.strings.printOptions = this.translate.instant(/* i18nextract */'StaffApp.DMS.Viewer.PrintModal.PrintOptions');
		this.options.strings.includeAnnotations =
			this.translate.instant(/* i18nextract */'StaffApp.DMS.Viewer.PrintModal.IncludeAnnotations');
		this.options.strings.includeComments = this.translate.instant(/* i18nextract */'StaffApp.DMS.Viewer.PrintModal.IncludeComments');
		this.options.strings.addWatermark = this.translate.instant(/* i18nextract */'StaffApp.DMS.Viewer.PrintModal.AddWatermark');
		this.options.strings.placement = this.translate.instant(/* i18nextract */'StaffApp.DMS.Viewer.WatermarkModal.Placement');
		this.options.strings.center = this.translate.instant(/* i18nextract */'StaffApp.DMS.Viewer.WatermarkModal.Center');
		this.options.strings.topLeft = this.translate.instant(/* i18nextract */'StaffApp.DMS.Viewer.WatermarkModal.TopLeft');
		this.options.strings.topRight = this.translate.instant(/* i18nextract */'StaffApp.DMS.Viewer.WatermarkModal.TopRight');
		this.options.strings.topCenter = this.translate.instant(/* i18nextract */'StaffApp.DMS.Viewer.WatermarkModal.TopCenter');
		this.options.strings.bottomLeft = this.translate.instant(/* i18nextract */'StaffApp.DMS.Viewer.WatermarkModal.BottomLeft');
		this.options.strings.bottomRight = this.translate.instant(/* i18nextract */'StaffApp.DMS.Viewer.WatermarkModal.BottomRight');
		this.options.strings.bottomCenter = this.translate.instant(/* i18nextract */'StaffApp.DMS.Viewer.WatermarkModal.BottomCenter');
		this.options.strings.text = this.translate.instant(/* i18nextract */'StaffApp.DMS.Viewer.WatermarkModal.Text');
		this.options.strings.font = this.translate.instant(/* i18nextract */'StaffApp.DMS.Viewer.WatermarkModal.Font');
		this.options.strings.size = this.translate.instant(/* i18nextract */'StaffApp.DMS.Viewer.WatermarkModal.Size');
		this.options.strings.opacity = this.translate.instant(/* i18nextract */'StaffApp.DMS.Viewer.WatermarkModal.Opacity');
		this.options.strings.restoreDefaultSettings =
			this.translate.instant(/* i18nextract */'StaffApp.DMS.Viewer.WatermarkModal.RestoreDefaultSettings');
		this.options.strings.add = this.translate.instant(/* i18nextract */'StaffApp.DMS.Viewer.WatermarkModal.Add');
		this.options.strings.cancel = this.translate.instant(/* i18nextract */'StaffApp.DMS.Viewer.WatermarkModal.Cancel');
		this.options.strings.fontStyle = this.translate.instant(/* i18nextract */'StaffApp.DMS.Viewer.WatermarkModal.FontStyle');
		this.options.strings.downloadOptions = this.translate.instant(/* i18nextract */'StaffApp.DMS.Viewer.DownloadModal.DownloadOptions');
		this.options.strings.downloadAsPdf = this.translate.instant(/* i18nextract */'StaffApp.DMS.Viewer.DownloadModal.DownloadAsPdf');
		this.options.strings.printWarningMessage =
			this.translate.instant(/* i18nextract */'StaffApp.DMS.Viewer.PrintModal.PrintWarningMessage');
	}

	public ngOnDestroy() {
		this._cancellationTokenSource?.cancel();
	}

	public viewerLoadingStateChange(state: boolean): void {
		this.viewerIsLoading = state;
	}

	private setPreviewDocumentIndex(): void {
		if (!Number.isNaN(this._currentIndex) && this._documents) {
			const document = this._documents[this._currentIndex];
			this._cancellationTokenSource?.reset();

			if (document) {
				this.document = document;
				this._documentMimeType = document.mimeType;
				this.configureWebViewerAppearance();
				void this.waitAndReportDocumentAccessed();
			}
		}
	}

	private async waitAndReportDocumentAccessed(): Promise<void> {
		await new Promise(c => setTimeout(c, 3000));
		this.onActionDoneOnDocument(this.document.id, DocumentEvent.Accessed);
	}

	private async setUserDisplayAsAndContactId() {
		const session = await this.getBindings();
		const companyId = session.ApplicationConfiguration.CompanyId;
		this.userContactId = session.ApplicationConfiguration.ContactId;
		const currentUser = await this.contactService.get(companyId, this.userContactId);
		this.userDisplayAs = currentUser.displayAs;
	}

	private canAddAnnotations(): boolean {
		const isAnotatableDocument = Object.values(AnnotatableFileMimeType).includes(this._documentMimeType as AnnotatableFileMimeType);

		return this._annotationsAllowed && isAnotatableDocument;
	}

	public canPreviewDocument(): boolean {
		if (this.document && (isWebViewerClientSupported(this.document.name) || this.document.canPreview)) {
			return true;
		}

		return false;
	}

	public downloadModalClosedHandler() {
		this.downloadModalClosed.emit();
	}

	public documentUpdateActionHandler() {
		this.documentUpdateAction.emit();
	}

	public isSearchGridOrDocumentActivity() {
		const path = this.location.path();
		if (path.includes('documents/alldocuments') || path.includes('documents/search')) {
			return true;
		}
		return false;
	}
	private async configureWebViewerAppearance(): Promise<void> {
		const options = Object.assign({}, this.options);
		this.shouldNotShowNewInsertScan = await this.hasIac('OnvioDrive.ScanLegacy').catch(() => false);
		options.headerOptions = {
			showOpenButton: true,
			showManageDropdown: true,
			showDownloadOption: true,
			showSendOption: true,
			showMoveOption: true,
			showCopyOption: true,
			showRenameOption: true,
			showDeleteOption: true,
			showUnlockOption: true,
			showInsertScanOption: !this.shouldNotShowNewInsertScan && !this.isSearchGridOrDocumentActivity(),
			showApproveOption: this.drawer === FileDrawer.Client,
			openDisabled: !!this.document?.lock,
			moveDisabled: !!this.document?.lock,
			renameDisabled: !!this.document?.lock,
			insertScanDisabled: this.document?.mimeType !== 'application/pdf' || !!this.document?.lock,
			deleteDisabled: !!this.document?.lock,
			unlockDisabled: this.isDocumentDisabledForEditOrApprovalOrEsign(this.document),
			approveDisabled: !!this.document?.lock,
		};
		options.showNavigationArrows = this.showNavigationArrows;
		options.showHeader = true;
		options.enablePrintHotKey = true;
		options.enableViewControlAndMenu = true;
		options.showPageNavOverlayButtons = true;
		options.disableEditingOutlines = !!this.document?.lock;

		const isPdfDocument = this._documentMimeType === AnnotatableFileMimeType.Pdf;

		options.enablePdfFeatures = isPdfDocument;
		options.zoom = 1.2;

		if (this.canPreviewDocument()) {
			options.disableDefaultPdfTronElements = false;
			options.enableViewControlAndMenu = true;
			options.showPageNavOverlayButtons = true;
		} else {
			options.disableDefaultPdfTronElements = true;
			options.enableViewControlAndMenu = false;
			options.showPageNavOverlayButtons = false;
			const elementsToErase = [...viewControlsDropdown, ...cogMenu, WebViewerElement.pageNavOverlay] as WebViewerElement[];
			this.removeAdditionalElements(options, elementsToErase);
		}

		options.readonly = !!this.document?.lock;
		options.editable = this.canAddAnnotations();
		options.disableFormInputs = options.readonly && isPdfDocument;

		this.options = options;
	}

	private removeAdditionalElements(options: Options, elements: WebViewerElement[]): WebViewerElement[] {
		return options.additionalPdfTronElements = options.additionalPdfTronElements.filter(item => !elements.includes(item));
	}

	private isDocumentDisabledForEditOrApprovalOrEsign(document: IDocument2): boolean {
		return !document || !document.lock ||
			(document.lock.type !== LockType.Edit
				&& document.lock.context !== LockContext.Approvals
				&& document.lock.context !== LockContext.Esign);
	}

	private onActionDoneOnDocument(id: string, action: DocumentEvent): void {
		if (this.actionDoneOnDocument) {
			this.actionDoneOnDocument.emit({ id, action });
		}
	}
}
